import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import GoldenStrip from "../GoldenStrip";
import EmptyState from "../../../Common/Components/EmptyState";
import ExploreMoreCities from "../ExploreMoreCities";

import "./SelectedCitySection.css";
import { useGetBusinessData, useVendorTypes } from "../../../Utility";
import {
  findImageForCategory,
  getTags,
  tranformBusinessData,
} from "../../../utils/UtilityFunctions";
import WvManagedBadge from "../WvManagedBadge";
import TopRibbon from "../TopRibbon";
import { useMemo } from "react";
import {
  buildHomeCityPath,
  buildVenuesListingPath,
  buildVendorsListingPath,
  getListingCityFromPathname,
} from "../../../utils/listingPaths";

function SelectedCitySection() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const selectedCity =
    getListingCityFromPathname(location.pathname) ||
    searchParams.get("city") ||
    "";

  const businessData = useGetBusinessData();
  const businessesList = useMemo(
    () => businessData?.businessesList ?? [],
    [businessData?.businessesList],
  );
  const filteredData = useMemo(() => {
    const list =
      businessesList?.filter(
        (venue) => venue?.city?.toLowerCase() === selectedCity?.toLowerCase(),
      ) ?? [];

    const topRank = list
      .filter((v) => v.top_rank !== null)
      .sort((a, b) => a.top_rank - b.top_rank)


    const others = list.filter((v) => v.top_rank === null);

    return [...topRank, ...others];
  }, [businessesList, selectedCity]);
  const filteredVenues = tranformBusinessData(filteredData, true);
  const vendorTypes = useVendorTypes(businessesList, false, selectedCity);
  const handleViewAllVendorsClick = () => {
    navigate(buildVendorsListingPath({ city: selectedCity }));
  };

  const handleCategoryClick = (categoryId) => {
    const category = categoryId
      ?.toLowerCase()
      ?.replace(`in ${selectedCity.toLowerCase()}`, "")
      ?.replace(/s$/, "");
    navigate(
      buildVendorsListingPath({ city: selectedCity, category }),
    );
  };
  if (!selectedCity) {
    return null;
  }
  const loading = businessData?.getBusinessesLoading;

  if (loading) {
    return (
      <>
        <section
          id="explore-section"
          className="selected-city-section selected-city-section-skeleton"
        >
          <GoldenStrip />
          <div className="selected-city-container">
            <div className="selected-city-skeleton-label skeleton-pulse" />
            <div className="selected-city-header">
              <div className="selected-city-skeleton-title skeleton-pulse" />
              <div className="selected-city-skeleton-btn skeleton-pulse" />
            </div>
            <div className="selected-city-venues-grid">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="selected-city-venue-card selected-city-skeleton-card"
                >
                  <div className="selected-city-skeleton-image skeleton-pulse" />
                  <div className="selected-city-venue-content">
                    <div className="selected-city-skeleton-line skeleton-pulse" />
                    <div className="selected-city-skeleton-line selected-city-skeleton-line-short skeleton-pulse" />
                    <div className="selected-city-venue-details">
                      <div className="selected-city-skeleton-line selected-city-skeleton-detail skeleton-pulse" />
                      <div className="selected-city-skeleton-line selected-city-skeleton-detail skeleton-pulse" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        <section
          id="top-vendors-section"
          className="selected-city-vendors-section selected-city-section-skeleton"
        >
          <div className="selected-city-container">
            <div className="selected-city-skeleton-label skeleton-pulse vendors-section-label" />
            <div className="vendors-section-header">
              <div className="vendors-header-content">
                <div className="selected-city-skeleton-title selected-city-skeleton-vendor-title skeleton-pulse" />
                <div className="selected-city-skeleton-line selected-city-skeleton-subtitle skeleton-pulse" />
              </div>
              <div className="selected-city-skeleton-btn skeleton-pulse" />
            </div>
            <div className="vendors-horizontal-scroll top-vendors-scroll">
              <div className="top-vendors-cards-container">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    className="top-vendor-card top-vendor-card-skeleton"
                    aria-hidden="true"
                  >
                    <div className="top-vendor-card-image-wrapper">
                      <div className="selected-city-skeleton-vendor-image skeleton-pulse" />
                    </div>
                    <div className="top-vendor-card-label-row">
                      <div className="selected-city-skeleton-vendor-label skeleton-pulse" />
                      <span
                        className="top-vendor-card-heart"
                        aria-hidden="true"
                        style={{ opacity: 0.3 }}
                      >
                        ♥
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      {/* Venues Section */}
      <section id="explore-section" className="selected-city-section">
        <GoldenStrip />
        <div className="selected-city-container">
          <span className="selected-city-section-label">Venues</span>
          <div className="selected-city-header">
            <h2 className="selected-city-title">
              Explore Venues in{" "}
              <span className="city-name">{selectedCity}</span>
            </h2>
            {filteredVenues.length > 0 && (
              <button
                className="selected-city-view-all"
                onClick={() =>
                  navigate(
                    buildVenuesListingPath("all", { city: selectedCity }),
                  )
                }
                type="button"
              >
                View All Venues
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            )}
          </div>

          {filteredVenues.length > 0 ? (
            <div className="selected-city-venues-grid">
              {filteredVenues?.map((venue) => (
                <div
                  key={venue.id}
                  className="selected-city-venue-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/venue/${venue.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/venue/${venue.id}`);
                    }
                  }}
                  aria-label={`View details for ${venue.name}`}
                >
                  <div className="selected-city-venue-image-wrapper">
                    {venue.top_rank != null && !Number.isNaN(Number(venue.top_rank)) && (
                      <TopRibbon label="Top Venues" />
                    )}
                    <img
                      src={venue.image}
                      alt={venue.name}
                      className="selected-city-venue-image"
                    />
                    <div className="selected-city-venue-overlay-badges">
                      {/* <span className="selected-city-venue-type-badge">
                        {venue.type}
                      </span> */}
                      <div className="selected-city-venue-tags-overlay">
                        {(() => {
                          const tagsSource = Array.isArray(venue?.tags)
                            ? venue.tags
                            : getTags(venue?.tags);

                          const restTags = tagsSource.filter(
                            (t) =>
                              !/^(Managed|Wz Managed|Wv Managed)$/i.test(
                                String(t).trim(),
                              ),
                          );

                          const showTags = restTags.slice(0, 2);
                          const hasMore = restTags.length > 2;

                          return (
                            <>
                              {showTags.map((tag) => (
                                <WvManagedBadge key={tag} text={tag.trim()} />
                              ))}

                              {hasMore && (
                                <span
                                  className="selected-city-venue-tag-ellipsis"
                                  aria-hidden="true"
                                >
                                  …
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="selected-city-venue-content">
                    <h3 className="selected-city-venue-name">{venue.name}</h3>
                    <p className="selected-city-venue-address">
                      <span
                        className="selected-city-venue-address-icon"
                        aria-hidden="true"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                      </span>
                      {venue.address}
                    </p>
                    <div className="selected-city-venue-stats">
                      <div className="selected-city-venue-stat">
                        <span
                          className="selected-city-venue-stat-icon"
                          aria-hidden="true"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                        </span>
                        <span className="selected-city-venue-stat-capacity-wrap">
                          <span className="selected-city-venue-stat-capacity-label">
                            Capacity
                          </span>
                          <span className="selected-city-venue-stat-text">
                            {venue.capacity.min} – {venue.capacity.max}
                          </span>
                        </span>
                      </div>
                      <span
                        className="selected-city-venue-stat-divider"
                        aria-hidden="true"
                      />
                      <div className="selected-city-venue-stat selected-city-venue-stat-price">
                        <span
                          className="selected-city-venue-stat-icon"
                          aria-hidden="true"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="8" />
                            <circle
                              cx="12"
                              cy="12"
                              r="5"
                              strokeOpacity="0.35"
                            />
                          </svg>
                        </span>
                        <span className="selected-city-venue-stat-price-wrap">
                          <span className="selected-city-venue-stat-text">
                            {venue.pricePerPlate.discounted} Per Plate
                          </span>
                        </span>
                      </div>
                    </div>
                    <div
                      className="selected-city-venue-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="selected-city-venue-btn selected-city-venue-btn-primary"
                        onClick={() => navigate(`/venue/${venue.id}`)}
                      >
                        View Details
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="selected-city-venue-btn selected-city-venue-btn-secondary"
                        onClick={() =>
                          navigate(
                            `/enquiry?venue=${encodeURIComponent(venue.id)}`,
                          )
                        }
                      >
                        Enquire
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No venues found"
              message={`We couldn't find any venues in ${selectedCity} yet. Try another city or check back later.`}
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              }
            />
          )}
        </div>
      </section>

      {/* Vendor categories – below Explore Venues (only appData 70–76 categories) */}
      <section
        id="top-vendors-section"
        className="selected-city-vendors-section"
      >
        <div className="selected-city-container">
          <span className="selected-city-section-label vendors-section-label">
            Vendors
          </span>
          <div className="vendors-section-header">
            <div className="vendors-header-content">
              <h2 className="vendors-section-title">
                Explore Vendors in{" "}
                <span className="city-name">{selectedCity}</span>
              </h2>
              <p className="vendors-section-subtitle">
                Explore photographers, caterers, decorators and more in{" "}
                {selectedCity}
              </p>
            </div>
            {vendorTypes.length > 0 && (
              <button
                className="vendors-view-all-btn"
                onClick={handleViewAllVendorsClick}
                type="button"
              >
                View All Vendors
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            )}
          </div>

          {vendorTypes.length > 0 ? (
            <div className="vendors-horizontal-scroll top-vendors-scroll">
              <div className="top-vendors-cards-container">
                {vendorTypes.map((cat) => (
                  <div
                    key={cat}
                    className="top-vendor-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleCategoryClick(cat)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCategoryClick(cat);
                      }
                    }}
                    aria-label={`View ${cat} in ${selectedCity}`}
                  >
                    <div className="top-vendor-card-image-wrapper">
                      <img
                        src={findImageForCategory(
                          businessesList,
                          cat,
                          selectedCity,
                        )}
                        alt={cat}
                        className="top-vendor-card-image"
                      />
                    </div>
                    <div className="top-vendor-card-label-row">
                      <span className="top-vendor-card-name">{cat}</span>
                      <span
                        className="top-vendor-card-heart"
                        aria-hidden="true"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="#e91e63"
                          stroke="#e91e63"
                          strokeWidth="1.5"
                        >
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EmptyState
              title="No vendors found"
              message={`We couldn't find any vendors in ${selectedCity} yet. Try another city or check back later.`}
            />
          )}
        </div>
      </section>

      <ExploreMoreCities
        currentCity={selectedCity}
        onCitySelect={(city) => navigate(buildHomeCityPath(city))}
        autoScroll
      />
    </>
  );
}

export default SelectedCitySection;
